import { useEffect, useState } from "react";
import { Toast } from "@sscs/ui";
import { useAppSelector } from "Hooks";
import {
  selectDeletedPackages,
  selectDeletedProjects,
  selectDeletedVersions,
} from "Store/projects.slice";
import { MoveToProjectWizardStep } from "../constants";

const useProjectNotification = (
  selectedProjectId: string,
  currentStep: MoveToProjectWizardStep,
) => {
  const [notified, setNotified] = useState<string[]>([]);
  const deletedProjects = useAppSelector(selectDeletedProjects);
  const isProjectDeleted = deletedProjects.some(
    (deletedProject) => deletedProject.id === selectedProjectId,
  );
  const isProjectDeletionNotified = notified.includes(selectedProjectId);

  useEffect(() => {
    const relevantStep = MoveToProjectWizardStep.SelectProjectStep;

    if (
      isProjectDeleted &&
      !isProjectDeletionNotified &&
      currentStep >= relevantStep
    ) {
      let warningMessage = "Selected project was deleted.";

      if (currentStep < relevantStep) {
        return;
      } else if (currentStep > relevantStep) {
        warningMessage += " Please restart from first step.";
      }

      Toast.show({
        title: warningMessage,
        type: "warning",
      });
      setNotified((prev) => [...prev, selectedProjectId]);
    }
  }, [
    isProjectDeleted,
    isProjectDeletionNotified,
    selectedProjectId,
    currentStep,
  ]);
};

const usePackageNotification = (
  selectedPackageId: string,
  currentStep: MoveToProjectWizardStep,
) => {
  const [notified, setNotified] = useState<string[]>([]);
  const deletedPackages = useAppSelector(selectDeletedPackages);

  const isPackageDeleted = deletedPackages.some(
    (deletedPackage) => selectedPackageId === deletedPackage.id,
  );

  const isPackageDeletionNotified = notified.some(
    (packageId) => packageId === selectedPackageId,
  );

  useEffect(() => {
    const relevantStep = MoveToProjectWizardStep.SelectPackageStep;

    if (
      isPackageDeleted &&
      !isPackageDeletionNotified &&
      currentStep >= relevantStep
    ) {
      let warningMessage = "Selected package was deleted.";

      if (currentStep > relevantStep) {
        warningMessage += " Please restart from second step.";
      }

      Toast.show({
        title: warningMessage,
        type: "warning",
      });
      setNotified((prev) => [...prev, selectedPackageId]);
    }
  }, [
    isPackageDeleted,
    isPackageDeletionNotified,
    selectedPackageId,
    currentStep,
  ]);
};

const useVersionNotification = (
  selectedVersionId: string,
  currentStep: MoveToProjectWizardStep,
) => {
  const [notified, setNotified] = useState<string[]>([]);
  const deletedPackageVersions = useAppSelector(selectDeletedVersions);

  const isVersionDeleted = deletedPackageVersions.some(
    (deletedVersion) => selectedVersionId === deletedVersion.id,
  );
  const isVersionDeletionNotified = notified.includes(selectedVersionId ?? "");

  useEffect(() => {
    const relevantStep = MoveToProjectWizardStep.SoftwareInfoStep;

    if (
      selectedVersionId &&
      isVersionDeleted &&
      !isVersionDeletionNotified &&
      currentStep >= relevantStep
    ) {
      let warningMessage = "Selected derived version was deleted.";

      if (currentStep > relevantStep) {
        warningMessage += " Please restart from third step.";
      }

      Toast.show({
        title: warningMessage,
        type: "warning",
      });
      setNotified((prev) => [...prev, selectedVersionId]);
    }
  }, [
    isVersionDeleted,
    isVersionDeletionNotified,
    selectedVersionId,
    currentStep,
  ]);
};

const useRestartNotification = (
  projectId: string | undefined,
  packageId: string | undefined,
  predecessorId: string | null,
  currentStep: MoveToProjectWizardStep,
) => {
  useProjectNotification(projectId ?? "", currentStep);
  usePackageNotification(packageId ?? "", currentStep);
  useVersionNotification(predecessorId ?? "", currentStep);
};

export default useRestartNotification;
